export type BouncingCommand = "WHIZ" | "BOUNCE" | "BOING";
export type BouncingDirection = 1 | -1;

export interface BouncingState {
  playerCount: number;
  currentSeat: number;
  direction: BouncingDirection;
  streak: number;
}

export interface BouncingMove {
  command: BouncingCommand;
  fromSeat: number;
  toSeat: number;
  skippedSeat: number | null;
  directionBefore: BouncingDirection;
  directionAfter: BouncingDirection;
}

export interface BouncingTurn {
  state: BouncingState;
  move: BouncingMove;
}

const MIN_PLAYERS = 3;
const MAX_PLAYERS = 8;

function assertPlayerCount(playerCount: number) {
  if (!Number.isInteger(playerCount) || playerCount < MIN_PLAYERS || playerCount > MAX_PLAYERS) {
    throw new RangeError(`Bouncing Ball playerCount must be ${MIN_PLAYERS}..${MAX_PLAYERS}`);
  }
}

function seatAfter(currentSeat: number, direction: BouncingDirection, hops: number, playerCount: number) {
  return ((currentSeat - 1 + direction * hops + playerCount * 4) % playerCount) + 1;
}

export function createBouncingState(playerCount = 4): BouncingState {
  assertPlayerCount(playerCount);
  return { playerCount, currentSeat: 1, direction: 1, streak: 0 };
}

export function advanceBouncingTurn(state: BouncingState, command: BouncingCommand): BouncingTurn {
  assertPlayerCount(state.playerCount);
  if (state.currentSeat < 1 || state.currentSeat > state.playerCount) throw new RangeError("Bouncing Ball currentSeat is outside playerCount");
  const directionAfter = command === "BOING" ? (state.direction * -1) as BouncingDirection : state.direction;
  const skippedSeat = command === "BOUNCE" ? seatAfter(state.currentSeat, directionAfter, 1, state.playerCount) : null;
  const toSeat = seatAfter(state.currentSeat, directionAfter, command === "BOUNCE" ? 2 : 1, state.playerCount);
  return {
    state: { ...state, currentSeat: toSeat, direction: directionAfter, streak: state.streak + 1 },
    move: {
      command,
      fromSeat: state.currentSeat,
      toSeat,
      skippedSeat,
      directionBefore: state.direction,
      directionAfter,
    },
  };
}

export function resetBouncingStreak(state: BouncingState): BouncingState {
  return { ...state, streak: 0 };
}
